const {getMemberDiscountParams} = require('../expedia_utils');
const appContext = require('od-modules/xsell/config/app_context');
const expediaUtils = require('../expedia_utils');
const hotelLabels = require('od-modules/xsell/config/hotel_touchpoint_labels');
const deeplinkBuilder = require('./deeplinks');
const accommodationPostBookingUrlService = require('od-modules/accommodation/accommodationPostBookingUrlService');
const isHsaAvailable = require('od-modules/hsa/hsaFunnelAvailable');
const Logger = require('od-modules/utils/logger');

const HOTELS = appContext.products.HOTELS;
const EXPEDIA_DATE_FORMAT = 'isoDate';
const ROOMS = 1;
const ROOM_NUMBER = 0;

function shouldUseUrlService(isPrime) {
    return isHsaAvailable(isPrime) && Odigeo.AB.isAccommodationDeepLinkPostBookingUrl;
}

function getHotelProvider() {
    return Odigeo.AB.isPostbookingXsellBanners
        ? 'postbooking'
        : appContext.getHotelProvider();
}

function hotelsDeeplink(itinerary, touchpoint, isPrime=false) {
    if (shouldUseUrlService(isPrime)) {
        try {
            return getUrlFromService(itinerary, touchpoint, getBookingUrl);
        } catch (e) {
            Logger.error('URL service failure, fallback to legacy deeplink', e);
        }
    }
    let urlPromise;
    switch (getHotelProvider()) {
        case 'postbooking':
            urlPromise = getPostBookingUrl(itinerary, touchpoint, isPrime);
            break;
        case 'expedia':
            urlPromise = getExpediaUrl(itinerary, touchpoint, isPrime);
            break;
        default:
            urlPromise = getBookingUrl(itinerary, touchpoint);
            break;
    }
    return urlPromise;
}

function applyExpediaParameters(itinerary, token, touchpoint, isPrime) {
    const arrivalLeg = itinerary.getArrivalSegment().getArrivalLeg();
    const dates = itinerary.getItineraryDates(HOTELS);
    return {
        'widgetTarget': 'XSellWidgetPlace',
        'airportArrival': arrivalLeg.getDestinationAirportIata(),
        'widgetID': expediaUtils.getExpediaWidgetId(isPrime),
        'q-check-in': Odigeo.Utils.formatDate(dates.arrival.arrivalDate, 'yyyy-mm-dd', true), // hotel checkin date
        'q-check-out': Odigeo.Utils.formatDate(dates.departure.departureDate, 'yyyy-mm-dd', true), // hotel checkout date
        ...travellersParams(itinerary.getTravellers().getTravellerList()),
        'locale': Odigeo.CrossSelling.locale,
        'cur': appContext.getCurrencyCode(),
        'wapb3': hotelLabels.getExpediaTracking(touchpoint, isPrime),
        'flightNumOfPassengers': itinerary.getNumberOfPassengers(),
        'flightOutboundDate': Odigeo.Utils.formatDate(dates.arrival.arrivalDate, 'yyyy-mm-dd', true), // flight arrival date
        'flightInboundDate': Odigeo.Utils.formatDate(dates.departure.departureDate, 'yyyy-mm-dd', true), // flight departure date
        'flightAirportOrigin': arrivalLeg.getOriginAirpotIata(),
        // purchase date, we don't have it in booking details
        'flightPurchaseDate': Odigeo.Utils.formatDate(appContext.getDate(), EXPEDIA_DATE_FORMAT),
        'token': token,
        ...getMemberDiscountParams(isPrime),
    };
}

function getAdults(travellers) {
    return Odigeo._.filter(travellers, (traveller) => traveller.getAgeType() === 'ADULT');
}

function getChildren(travellers) {
    return Odigeo._.filter(travellers, (traveller) => traveller.getAgeType() === 'INFANT' || traveller.getAgeType() === 'CHILD');
}

function travellersParams(travellers) {
    const adults = getAdults(travellers);
    const children = getChildren(travellers);

    let travellerParams = {'q-rooms': ROOMS};
    if (adults.length > 0) {
        Odigeo._.extend(travellerParams, {[`q-room-${ROOM_NUMBER}-adults`]: adults.length},);
    }
    if (children.length > 0) {
        Odigeo._.extend(travellerParams, {[`q-room-${ROOM_NUMBER}-children`]: children.length});
        children.forEach((value, key) => {
            Odigeo._.extend(travellerParams, {[`q-room-${ROOM_NUMBER}-child-${key}-age`]: value.getAge()});
        });
    }
    return travellerParams;
}

function getExpediaUrl(itinerary, touchpoint, isPrime) {
    return expediaUtils.getToken(prepareExpediaToken(itinerary))
        .then((token) => applyExpediaParameters(itinerary, token, touchpoint, isPrime))
        .then(function(expediaParams) {
            return deeplinkBuilder()
                .withBaseUrl(Odigeo.CrossSelling.expedia.baseUrl)
                .withQueryParams(expediaParams)
                .build();
        });
}

function getBookingUrl(itinerary, touchpoint) {
    const arrivalLeg = itinerary.getArrivalSegment().getArrivalLeg();
    const dates = itinerary.getItineraryDates(HOTELS);

    const params = {
        iata: arrivalLeg.getDestinationAirportIata(),
        checkin_monthday: Odigeo.Utils.formatDate(dates.arrival.arrivalDate, 'dd', true),
        checkin_year_month: Odigeo.Utils.formatDate(dates.arrival.arrivalDate, 'yyyy-mm', true),
        checkout_monthday: Odigeo.Utils.formatDate(dates.departure.departureDate, 'dd', true),
        checkout_year_month: Odigeo.Utils.formatDate(dates.departure.departureDate, 'yyyy-mm', true),
        selected_currency: appContext.getCurrencyCode(),
        lang: appContext.getLocale().split('_')[0],
        label: hotelLabels.getBookingTracking(touchpoint),
        ...(Odigeo.CrossSelling.bookingcom.aid && {aid: Odigeo.CrossSelling.bookingcom.aid}),
        ...getBookingMobileParams(),
    };
    return Promise.resolve(deeplinkBuilder()
        .withBaseUrl(Odigeo.CrossSelling.bookingcom.baseUrl)
        .withQueryParams(params)
        .build());
}

function getBookingMobileParams() {
    return appContext.getFlow() === 'mobile' ? {'sid': 12345678, 'order': 'price_for_two'} : '';
}

function prepareExpediaToken(itinerary) {
    const arrivalLeg = itinerary.getArrivalSegment().getArrivalLeg();
    const dates = itinerary.getItineraryDates(HOTELS);

    let concatString = '';
    concatString += Odigeo.Utils.formatDate(dates.arrival.arrivalDate, EXPEDIA_DATE_FORMAT, true) + '-';
    concatString += Odigeo.Utils.formatDate(dates.departure.departureDate, EXPEDIA_DATE_FORMAT, true) + '-';
    concatString += itinerary.getNumberOfPassengers() + '-';
    concatString += arrivalLeg.getOriginAirpotIata() + '-';
    concatString += Odigeo.Utils.formatDate(appContext.getDate(), EXPEDIA_DATE_FORMAT); // purchase date, we don't have it in booking details

    return concatString;
}

function getPostBookingUrl(itinerary, touchpoint, isPrime) {
    const buyer = itinerary.getBuyer();
    const encodedMail = btoa(buyer.getMail());
    const buyPath = hotelLabels.getBuyPath(touchpoint) || '';

    const params = {
        product: 'accommodation',
        trackingId: encodedMail,
        bookingId: itinerary.getBookingId(),
        wapb3: hotelLabels.getExpediaTracking(touchpoint, isPrime),
        label: hotelLabels.getBookingTracking(touchpoint),
    };
    if (buyPath) {
        params['buyPath'] = buyPath;
    }

    return Promise.resolve(
        deeplinkBuilder()
            .withInternalPage('post_booking')
            .withQueryParams(params)
            .build());
}

function getUrlFromService(itinerary, touchpoint) {
    return accommodationPostBookingUrlService.get({
        bookingId: itinerary.getBookingId(),
        email: itinerary.getBuyer().getMail(),
        buyPath: hotelLabels.getBuyPath(touchpoint),
    });
}

module.exports = {
    parse: hotelsDeeplink,
    getExpediaUrl,
    getPostBookingUrl,
};
